"use client";

import { usePathname } from "next/navigation";
import Tabs, { TAB_STYLE, TabItem } from "@/components/common/Tab";
import SubpageBackground from "@/components/common/SubpageBackground";
import FloatingParticles from "@/components/common/FloatingParticles";
import PageTitle from "@/components/common/PageTitle";

export default function CompanyLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();

  // Check if we're on a JD detail page (careers layout will handle these)
  const isJDDetailPage = pathname.startsWith("/company/careers/") && pathname !== "/company/careers";

  // For JD detail pages, just render children (careers layout handles the header)
  if (isJDDetailPage) {
    return <>{children}</>;
  }

  const tabs: TabItem[] = [
    {
      id: "1",
      name: "About Us",
      value: "/company/about-us",
      href: "/company/about-us",
    },
    {
      id: "2",
      name: "Team",
      value: "/company/team",
      href: "/company/team",
    },
    {
      id: "3",
      name: "Customers",
      value: "/company/customers",
      href: "/company/customers",
    },
    // Careers is hidden from navigation on request — also taken out of the
    // COMPANY mega menu (Header.tsx) and the Footer. The route and its six JD
    // pages still build and render, so restoring it is putting the three menu
    // entries back; nothing under /company/careers had to be deleted. getTitle()
    // below still answers "Careers" for that reason.
  ];

  // Determine the title based on the current path
  const getTitle = () => {
    if (pathname.includes("about-us")) return "About ECQ";
    if (pathname.includes("team")) return "Team";
    if (pathname.includes("customers")) return "Customers";
    if (pathname.includes("careers")) return "Careers";
    return "Company";
  };

  // Supporting description shown under the title (only some pages have one)
  const getDescription = () => {
    if (pathname.includes("team"))
      return "Behind every successful project is a dedicated team committed to excellence. At ECQ, our achievements reflect the expertise, innovation, and collaboration of our professionals, who consistently deliver high-quality solutions and create lasting value for our clients.";
    if (pathname.includes("customers"))
      return "The trust of our customers is our greatest achievement. Their feedback reflects the quality of our services, our commitment to excellence, and the long-term partnerships we strive to build with every client.";
    return null;
  };

  const description = getDescription();

  return (
    <div className="relative min-h-screen flex flex-col bg-background pb-16 sm:pb-[120px]">
      {/* Decorative background moved to `next/image` to reduce CLS. */}
      {/* Company section uses the hexagon/tech pattern from Figma at 30% opacity. */}
      <SubpageBackground
        src="/images/subpage-background-tech.png"
        width={1548}
        height={1027}
        className="opacity-30"
      />

      {/* Ambient drift over the static pattern — the same layer the product
          pages carry, now shared rather than pasted per layout. */}
      <FloatingParticles className="z-[1]" />

      <div className="relative z-10 flex flex-col flex-1">
        {/* Title and Tab Section */}
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <section className="w-full max-w-[1300px] mx-auto pb-6 pt-[112px] md:pt-[128px] md:pb-[48px]">
            {/* All five titles fit on one line at 390px — nothing to reserve. */}
            <PageTitle className="mb-4 md:mb-6">{getTitle()}</PageTitle>

            {description && (
              <p className="text-white text-[15px] md:text-base leading-[26px] md:leading-[28px] tracking-[0.5px] max-w-[1000px] mb-6 md:mb-8">
                {description}
              </p>
            )}

            {/* Tab Navigation */}
            <Tabs
              tabs={tabs}
              currentValue={pathname}
              style={TAB_STYLE.STYLE_1}
              showContent={false}
              useRouter={true}
              customClassName="[&>a:first-child]:!pl-0"
            />
          </section>
        </div>

        {/* Page Content */}
        <main className="flex-1 w-full">{children}</main>
      </div>
    </div>
  );
}
